import { useQuery } from '@tanstack/react-query';

import { API_BASE_URL, ApiError, api } from '@/config/api';

function humanizeFetchError(error: unknown): string {
  if (error instanceof ApiError) {
    if ('message' in error.body && typeof error.body.message === 'string' && error.body.message.trim()) {
      return error.body.message;
    }
    return error.message.replace(/_/g, ' ');
  }
  if (error instanceof Error) return error.message;
  if (error) return String(error);
  return '';
}

/**
 * Thin wrapper around React Query + api.
 * Re-fetches whenever `url` changes. Pass `null` to skip.
 */
export function useFetch<T>(url: string | null) {
  const { data, error, isLoading, refetch } = useQuery<T>({
    queryKey: [url],
    queryFn: async () => {
      const path = url!.startsWith(API_BASE_URL) ? url!.slice(API_BASE_URL.length) : url!;
      return api<T>(path);
    },
    enabled: url != null,
  });

  return {
    data: data ?? null,
    /** Human-readable message (never a bare snake_case code alone when body.message exists). */
    error: humanizeFetchError(error),
    /** Raw query error for callers that need status/code (e.g. top-of-page banners). */
    errorCause: error ?? null,
    isLoading,
    refetch,
  };
}
